import type { AxiosResponse } from 'axios'
import { useCallback } from 'react'
import { useQueryClient } from 'react-query'

import { LabsFlag } from '../../../enums/labsFlag'
import { useLabsMenuFlags } from '../../../hooks/useLabsMenuFlags'
import { useOrganizationUiSetup } from '../../../hooks/useOrganizationUiSetup'
import { zervantSettingsQueryKey } from '../constants/zervantSettingsQueryKey'
import type { ZervantSettings } from '../types/zervantSettings'
import { copyInvoiceModelToEstimate } from '../utils/copyInvoiceModelToEstimate'

// Until the Quote settings section launches, every settings save for a Shine Invoicing
// organization also writes the invoice model into the estimate model, while the Invoicing
// Settings labs flag is off. That covers the old settings page and the shared writer.
// Removed with SILC-3209.
export const useCopyInvoiceToEstimateUntilLaunch = () => {
  const queryClient = useQueryClient()
  const { isUiSetupShineInvoicingAccounting } = useOrganizationUiSetup()
  const labsFlags = useLabsMenuFlags()

  const shouldCopy = isUiSetupShineInvoicingAccounting && !labsFlags[LabsFlag.InvoicingSettings]

  return useCallback(
    <TPayload extends Partial<ZervantSettings>>(payload: TPayload): TPayload => {
      if (!shouldCopy) {
        return payload
      }

      // Read the cache when the save runs, so the "still a copy" check sees the stored document.
      const cachedResponse = queryClient.getQueryData<AxiosResponse<ZervantSettings>>(zervantSettingsQueryKey)

      return copyInvoiceModelToEstimate(payload, cachedResponse?.data)
    },
    [queryClient, shouldCopy],
  )
}
